import { useState } from 'react';
import { useStudents, useClasses, useSubmit } from '../hooks';
import { Table, Modal, PageHeader, SearchBar, Avatar, StatusBadge, FormField, Select, ConfirmDialog, Badge, InfoRow } from '../components/UI';
import { Plus, Eye, Edit, Trash2, Download, UserPlus } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const GENDER_OPTS = [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }];
const FEE_OPTS = [{ value: 'general', label: 'General' }, { value: 'sc', label: 'SC' }, { value: 'st', label: 'ST' }, { value: 'obc', label: 'OBC' }];
const BLOOD_OPTS = ['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(v => ({ value: v, label: v }));

const initForm = {
  full_name: '', email: '', phone: '', password: '',
  class_id: '', roll_no: '', dob: '', gender: '',
  parent_name: '', parent_phone: '', parent_whatsapp: '',
  blood_group: '', fee_category: 'general', admission_date: '',
  address: '', photo: null,
};

export default function Students() {
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [showView, setShowView] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [form, setForm] = useState(initForm);
  const [photoPreview, setPhotoPreview] = useState(null);

  const { students, loading, total, refetch } = useStudents({ search, class_id: classFilter, page, limit: 15 });
  const { classes } = useClasses();
  const { loading: submitting, submit } = useSubmit();

  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setField('photo', file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v && k !== 'photo') fd.append(k, v); });
    if (form.photo) fd.append('photo', form.photo);
    const result = await submit(() => api.post('/students', fd), 'Student added successfully!');
    if (result.success) { setShowAdd(false); setForm(initForm); setPhotoPreview(null); refetch(); }
  };

  const handleDelete = async () => {
    const result = await submit(() => api.delete(`/students/${showDelete}`), 'Student deleted');
    if (result.success) { setShowDelete(null); refetch(); }
  };

  const StudentForm = () => (
    <form onSubmit={handleAdd}>
      {/* Photo Upload */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <label style={{ cursor: 'pointer' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', border: '3px dashed var(--border-strong)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-alt)' }}>
              {photoPreview
                ? <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>Click to upload photo</span>
              }
            </div>
          </div>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
        </label>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>Student Photo (optional)</div>
      </div>

      <div className="form-grid">
        <FormField label="Full Name" required><input className="form-control" value={form.full_name} onChange={e => setField('full_name', e.target.value)} required /></FormField>
        <FormField label="Email" required><input className="form-control" type="email" value={form.email} onChange={e => setField('email', e.target.value)} required /></FormField>
        <FormField label="Phone"><input className="form-control" value={form.phone} onChange={e => setField('phone', e.target.value)} /></FormField>
        <FormField label="Password" required><input className="form-control" type="password" value={form.password} onChange={e => setField('password', e.target.value)} placeholder="Default: Student@123" /></FormField>
        <FormField label="Class" required><Select value={form.class_id} onChange={v => setField('class_id', v)} options={classOpts} placeholder="Select Class" /></FormField>
        <FormField label="Roll No"><input className="form-control" type="number" value={form.roll_no} onChange={e => setField('roll_no', e.target.value)} /></FormField>
        <FormField label="Date of Birth"><input className="form-control" type="date" value={form.dob} onChange={e => setField('dob', e.target.value)} /></FormField>
        <FormField label="Gender"><Select value={form.gender} onChange={v => setField('gender', v)} options={GENDER_OPTS} /></FormField>
        <FormField label="Blood Group"><Select value={form.blood_group} onChange={v => setField('blood_group', v)} options={BLOOD_OPTS} /></FormField>
        <FormField label="Fee Category"><Select value={form.fee_category} onChange={v => setField('fee_category', v)} options={FEE_OPTS} /></FormField>
        <FormField label="Admission Date"><input className="form-control" type="date" value={form.admission_date} onChange={e => setField('admission_date', e.target.value)} /></FormField>
        <FormField label="Parent Name" required><input className="form-control" value={form.parent_name} onChange={e => setField('parent_name', e.target.value)} required /></FormField>
        <FormField label="Parent Phone"><input className="form-control" value={form.parent_phone} onChange={e => setField('parent_phone', e.target.value)} /></FormField>
        <FormField label="Parent WhatsApp"><input className="form-control" value={form.parent_whatsapp} onChange={e => setField('parent_whatsapp', e.target.value)} placeholder="For attendance alerts" /></FormField>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? <><span className="spinner" /> Adding...</> : <><UserPlus size={15} /> Add Student</>}
        </button>
      </div>
    </form>
  );

  const columns = [
    { label: 'Student', render: r => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar src={r.photo_url} name={r.full_name} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{r.full_name}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.admission_no}</div>
        </div>
      </div>
    )},
    { label: 'Class', render: r => <span style={{ fontWeight: 600 }}>{r.class_name} {r.section}</span> },
    { label: 'Roll No', key: 'roll_no' },
    { label: 'Parent', render: r => (
      <div>
        <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.parent_name}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.parent_phone}</div>
      </div>
    )},
    { label: 'WhatsApp', render: r => r.parent_whatsapp
      ? <span style={{ color: '#25d366', fontWeight: 600, fontSize: '0.8rem' }}>✓ {r.parent_whatsapp}</span>
      : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Not set</span>
    },
    { label: 'Status', render: r => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
    { label: 'Actions', render: r => (
      <div className="table-actions">
        <button className="btn btn-secondary btn-sm btn-icon" title="View" onClick={() => setShowView(r)}><Eye size={14}/></button>
        <button className="btn btn-secondary btn-sm btn-icon" title="Edit" onClick={() => { setShowEdit(r); setForm({...r, password:''}); }}><Edit size={14}/></button>
        <button className="btn btn-danger btn-sm btn-icon" title="Delete" onClick={() => setShowDelete(r.student_id)}><Trash2 size={14}/></button>
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Students" subtitle={`${total} students enrolled`}
        actions={
          <>
            <SearchBar value={search} onChange={setSearch} placeholder="Search name or admission no..." />
            <Select value={classFilter} onChange={setClassFilter} options={classOpts} placeholder="All Classes" />
            <button className="btn btn-primary" onClick={() => { setForm(initForm); setPhotoPreview(null); setShowAdd(true); }}>
              <Plus size={15} /> Add Student
            </button>
          </>
        } />

      <div className="card">
        <Table columns={columns} data={students} loading={loading} emptyMsg="No students found" />
      </div>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add New Student" size="lg">
        <StudentForm />
      </Modal>

      {/* View Modal */}
      <Modal open={!!showView} onClose={() => setShowView(null)} title="Student Details" size="">
        {showView && (
          <div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 24, padding: '16px', background: 'var(--surface-alt)', borderRadius: 'var(--r-md)' }}>
              <Avatar src={showView.photo_url} name={showView.full_name} size="avatar-xl" />
              <div>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>{showView.full_name}</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{showView.admission_no}</p>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <Badge type="info">{showView.class_name} {showView.section}</Badge>
                  <StatusBadge status={showView.is_active ? 'active' : 'inactive'} />
                </div>
              </div>
            </div>
            <InfoRow label="Date of Birth" value={showView.dob} />
            <InfoRow label="Gender" value={showView.gender} />
            <InfoRow label="Blood Group" value={showView.blood_group} />
            <InfoRow label="Roll No" value={showView.roll_no} />
            <InfoRow label="Email" value={showView.email} />
            <InfoRow label="Phone" value={showView.phone} />
            <InfoRow label="Parent Name" value={showView.parent_name} />
            <InfoRow label="Parent Phone" value={showView.parent_phone} />
            <InfoRow label="Parent WhatsApp" value={showView.parent_whatsapp} />
            <InfoRow label="Fee Category" value={showView.fee_category?.toUpperCase()} />
            <InfoRow label="Admission Date" value={showView.admission_date} />
          </div>
        )}
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog open={!!showDelete} onClose={() => setShowDelete(null)} onConfirm={handleDelete}
        title="Delete Student" message="This will permanently delete the student and all their records." loading={submitting} />
    </div>
  );
}
